/**
 * `name_capture` renderer — composes `@nextday/funnel-ui` `NameCapture`
 * (with the E4 extension: presets + onPresetSelected).
 *
 * Stores the typed name under `data.answerKey`. Selecting a preset that
 * carries a `value` writes the value through `onAnswer`; presets without a
 * `value` are passthroughs (the orchestrator can resolve them, e.g. fetch
 * the user's first name from the auth session).
 *
 * Step JSON shape (plan §5 step 7):
 *
 *   {
 *     "type": "name_capture",
 *     "data": {
 *       "question": "What should your SecretMate call you?",
 *       "description": "Optional — you can skip this for now.",
 *       "required": false,
 *       "maxLength": 40,
 *       "placeholder": "Your name or nickname",
 *       "answerKey": "display_name",
 *       "presets": [
 *         { "id": "first_name", "label": "Use my first name" },
 *         { "id": "nickname",   "label": "A nickname" }
 *       ]
 *     }
 *   }
 */
import {
  NameCapture,
  type NameCapturePreset,
  type NameCapturePreview,
  type NameCaptureReason,
} from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";
import { urlOrTemplate } from "@survey-kit/urlOrTemplate";

const presetSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /**
   * Optional value applied to the input on tap. When omitted, tapping just
   * fires the preset selection — the orchestrator can listen for this in
   * future work (e.g. fetching the auth session's first name).
   */
  value: z.string().optional(),
});

const previewSchema = z.object({
  template: z.string().min(1),
  followUp: z.string().optional(),
  avatarUrl: urlOrTemplate.optional(),
});

const reasonSchema = z.object({
  icon: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
});

export const nameCaptureSchema = z
  .object({
    question: z.string().min(1).optional(),
    description: z.string().optional(),
    eyebrow: z.string().optional(),
    answerKey: z.string().min(1),
    required: z.boolean().optional(),
    minLength: z.number().int().nonnegative().optional(),
    maxLength: z.number().int().positive().optional(),
    placeholder: z.string().optional(),
    presets: z.array(presetSchema).optional(),
    preview: previewSchema.optional(),
    reasons: z.array(reasonSchema).optional(),
    ctaLabel: z.string().min(1).optional(),
  })
  .strict();

export type NameCaptureData = z.infer<typeof nameCaptureSchema>;

const NameCaptureRenderer = ({
  data,
  step,
  answers,
  onAnswer,
  onValidityChange,
}: RendererProps<NameCaptureData>) => {
  const initial =
    typeof answers[data.answerKey] === "string" ? (answers[data.answerKey] as string) : "";

  const presets: NameCapturePreset[] | undefined = data.presets?.map((preset) => ({
    id: preset.id,
    label: preset.label,
    ...(preset.value !== undefined ? { value: preset.value } : {}),
  }));

  const preview: NameCapturePreview | undefined = data.preview
    ? {
        template: data.preview.template,
        followUp: data.preview.followUp,
        avatarUrl: data.preview.avatarUrl,
      }
    : undefined;

  const reasons: NameCaptureReason[] | undefined = data.reasons?.map((r) => ({
    icon: r.icon,
    title: r.title,
    description: r.description,
  }));

  return (
    <NameCapture
      question={data.question}
      description={data.description}
      eyebrow={data.eyebrow}
      initialValue={initial}
      required={data.required}
      minLength={data.minLength}
      maxLength={data.maxLength}
      placeholder={data.placeholder}
      presets={presets}
      preview={preview}
      reasons={reasons}
      onChange={(value) => onAnswer(data.answerKey, { value })}
      onValidityChange={onValidityChange}
      testId={`renderer-name-capture-${step.id}`}
    />
  );
};

export const nameCaptureRenderer = createRenderer({
  type: "name_capture",
  dataSchema: nameCaptureSchema,
  label: "Name capture",
  Component: NameCaptureRenderer,
});

export default nameCaptureRenderer;
